import { WorkflowsIcon } from '@persona-id/design-tokens/icons/12px';
import produce from 'immer';
import jsonLogic, { ReservedOperations } from 'json-logic-js';
import { get, set } from 'lodash/fp';

import { COMPONENT_JSONLOGIC_KEYS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  DEFAULT_LOGIC_RULE_BLOCK,
  INVERTED_RULE_SUFFIX,
  SERVER_EVALUATED_SUFFIX,
  UNARY_OPERATORS,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/constants';
import {
  ComponentJsonLogicKey,
  ComponentJsonLogicRule,
  DisableableLogicKey,
  HideableLogicKey,
  InvertedComponentJsonLogicKey,
  LogicRuleSection,
  UiDisableableComponent,
  UiDisableableComponentConfig,
  UiHideableComponent,
  UiHideableComponentConfig,
  UnaryOperator,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  ConditionalRule,
  ConditionalRuleValueAll,
  JsonLogicField,
  JsonLogicValue,
  LogicBlockOrV2,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { ComponentClientJsonLogicOperator, safeApply } from 'inquiry/lib/jsonLogic';
import { FieldSchemaType } from 'lib/fields';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { UiComponent, UiComponentConfig } from 'lib/interfaces/ui-components';

export const isClientLogicKey = (logicKey: ComponentJsonLogicKey) =>
  !logicKey.endsWith(SERVER_EVALUATED_SUFFIX);

export const isInvertedRuleConfig = (rule: ComponentJsonLogicRule) => {
  const hasParentInvertOperator = rule != null && '!' in rule && Object.keys(rule).length === 1;
  if (hasParentInvertOperator) {
    const nestedRules = get(['!'], rule);
    if (Array.isArray(nestedRules) && nestedRules.length > 1) return false;
    const nestedRuleKeys = Object.keys(nestedRules);
    if (nestedRuleKeys.length === 1 && nestedRuleKeys[0] === 'var') return false;
    return true;
  } else {
    return false;
  }
};

export const getNormalizedLogicKey = (
  logicKey: ComponentJsonLogicKey,
  isInvertedRule: boolean,
): ComponentJsonLogicKey | InvertedComponentJsonLogicKey => {
  return isInvertedRule ? getInvertedLogicKey(logicKey) : getDefaultStateLogicKey(logicKey);
};

export const getDefaultStateLogicKey = (
  key: ComponentJsonLogicKey | InvertedComponentJsonLogicKey,
): ComponentJsonLogicKey => key.replace(INVERTED_RULE_SUFFIX, '') as ComponentJsonLogicKey;

export const getInvertedLogicKey = (key: ComponentJsonLogicKey): InvertedComponentJsonLogicKey =>
  `${key}${INVERTED_RULE_SUFFIX}`;

export const isInvertedLogicKey = (
  key: ComponentJsonLogicKey | InvertedComponentJsonLogicKey,
): key is InvertedComponentJsonLogicKey => key.endsWith(INVERTED_RULE_SUFFIX);

const getDefaultBlockWrappedRules = (
  rules: ComponentJsonLogicRule,
  path: string[] = ['or', '0', 'and', '0'],
): LogicBlockOrV2 => {
  return set(path, rules, DEFAULT_LOGIC_RULE_BLOCK.rules);
};

const formatJSONLogicRuleRecursive = (
  rules: ComponentJsonLogicRule,
  selfReferenceToNormalize: string | undefined,
): ComponentJsonLogicRule => {
  const operator = jsonLogic.get_operator(rules);
  const values = jsonLogic.get_values(rules);
  switch (true) {
    case operator === 'or':
      // wrap missing 'and' blocks
      return {
        or: values.map((value: ConditionalRule) =>
          jsonLogic.get_operator(value) !== 'and'
            ? { and: [formatJSONLogicRuleRecursive(value, selfReferenceToNormalize)] }
            : formatJSONLogicRuleRecursive(value, selfReferenceToNormalize),
        ),
      };
    case operator === 'and':
      return {
        and: values.map((rule: ComponentJsonLogicRule) =>
          formatJSONLogicRuleRecursive(rule, selfReferenceToNormalize),
        ),
      };
    case operator === 'var':
      return { '!!': [rules as JsonLogicField] };
    case UNARY_OPERATORS.includes(operator as UnaryOperator):
      // wrap missing right-hand array values
      return Array.isArray(values) ? rules : { [operator]: [values] };
    default: {
      if (
        Array.isArray(values) &&
        values.length === 2 &&
        selfReferenceToNormalize != null &&
        Object.keys(values[0])[0] === 'var'
      ) {
        // intent here is to convert 'var: form.[component_name]' to 'var: value'
        // as the usage is the same and allows us to simplify the jsonSchema provided
        // to the JSON logic config inputs used to build the rules
        if (Object.values(values[0])[0] === selfReferenceToNormalize) {
          return { [operator]: [{ var: 'value' }, values[1]] };
        } else {
          return rules;
        }
      } else {
        return rules;
      }
    }
  }
};

/**
 * Helper to format inline JSONLogic rules to be nested in an `or`
 * block if the rule is not.
 * @returns Formatted rule via ComponentConditional type
 */
export const formatJSONLogicRule = (
  rules: ComponentJsonLogicRule | null,
  selfReferenceToNormalize?: string,
): { rules: LogicBlockOrV2; isInvertedRule: boolean } => {
  let formattedRules = rules ?? DEFAULT_LOGIC_RULE_BLOCK.rules;

  const isInvertedRule = isInvertedRuleConfig(formattedRules);
  if (isInvertedRule) {
    formattedRules = get(['!'], formattedRules);
    // if its inverted and is an array it must contain extraneous array
    formattedRules = Array.isArray(formattedRules) ? formattedRules[0] : formattedRules;
  }

  const operator = jsonLogic.get_operator(formattedRules);
  const values = jsonLogic.get_values(formattedRules);

  if (operator === '!!' && !Array.isArray(values)) {
    formattedRules = values;
  }

  if (operator !== 'or') {
    formattedRules = getDefaultBlockWrappedRules(formattedRules, ['or', '0']);
  }

  return {
    rules: formatJSONLogicRuleRecursive(formattedRules, selfReferenceToNormalize) as LogicBlockOrV2,
    isInvertedRule,
  };
};

export const buildRule = (
  operator: WorkflowOperatorKey,
  leftValue: JsonLogicValue,
  rightType: RuleValueTypes,
  rightValue?: JsonLogicValue,
): ConditionalRule => {
  let rightSide: ConditionalRuleValueAll | null = null;
  if (rightValue != null && rightType === RuleValueTypes.Field) {
    rightSide = { var: rightValue.toString() };
  } else if (rightValue != null && rightType === RuleValueTypes.Value) {
    rightSide = rightValue;
  }
  return rightSide != null
    ? {
        [operator]: [
          {
            var: leftValue,
          },
          rightSide,
        ],
      }
    : {
        [operator]: [
          {
            var: leftValue,
          },
        ],
      };
};

type SupportedClientJsonLogicOperator =
  | Extract<ReservedOperations, '==' | '!=' | '!' | '!!' | '>' | '>=' | '<' | '<=' | 'in'>
  | ComponentClientJsonLogicOperator;

const SUPPORTED_CLIENT_JSON_LOGIC_OPERATORS: SupportedClientJsonLogicOperator[] = [
  '==',
  '!=',
  '!',
  '!!',
  '>',
  '>=',
  '<',
  '<=',
  'in',
  'scalarlength',
];

export const isSupportedClientJsonLogicOperator = (
  operator: WorkflowOperatorKey | SupportedClientJsonLogicOperator,
): operator is SupportedClientJsonLogicOperator => {
  return SUPPORTED_CLIENT_JSON_LOGIC_OPERATORS.includes(
    operator as SupportedClientJsonLogicOperator,
  );
};

export const getOperatorsMap = (
  logicRuleSection: LogicRuleSection,
): Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>> => {
  const operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>> = {
    [FieldSchemaType.String]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.ContainsAnySubstring,
      WorkflowOperatorKey.ContainsNoSubstring,
      WorkflowOperatorKey.StartsWith,
      WorkflowOperatorKey.MatchesRegex,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
    [FieldSchemaType.Boolean]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
    [FieldSchemaType.Date]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
    [FieldSchemaType.Number]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
    [FieldSchemaType.Integer]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
    [FieldSchemaType.Choices]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
    [FieldSchemaType.MultiChoices]: [
      WorkflowOperatorKey.Contains,
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.IncludesAll,
      WorkflowOperatorKey.IncludesNone,
      WorkflowOperatorKey.IncludesNotAll,
      WorkflowOperatorKey.IncludesOnly,
      WorkflowOperatorKey.IncludesExactly,
      WorkflowOperatorKey.IncludesNotExactly,
      WorkflowOperatorKey.IsFalsy,
      WorkflowOperatorKey.IsTruthy,
    ],
  };

  return logicRuleSection === 'client'
    ? produce(operatorsMap, (draftMap) => {
        const mapKeys = Object.keys(draftMap) as Array<keyof typeof draftMap>;
        mapKeys.forEach((key) => {
          draftMap[key] = (draftMap[key] ?? []).filter(isSupportedClientJsonLogicOperator);
        });
      })
    : operatorsMap;
};

export const componentTotalNumConditionalConfigsWithRulesLogic = (
  component: UiComponentConfig | null,
) => {
  const components = component != null ? [component] : [];
  const logicConfigInfo = getConfigOnComponentWithJSONLogic(components);
  return componentConfigLogicUI(
    logicConfigInfo.size > 0
      ? [...getConfigOnComponentWithJSONLogic(components)][0].numConfigFields
      : 0,
  );
};

export const uiStepTotalNumComponentsWithRulesLogicOnConfig = (
  step: InquiryTemplateVersionStepUi | null,
  condition?: (component: UiComponentConfig) => boolean,
) => {
  let totalCount = 0;

  if (step != null) {
    const allComponents = getConfigOnComponentWithJSONLogic(uiStepAllComponentConfigs(step));
    totalCount =
      condition != null
        ? [...allComponents].filter(({ component }) => condition(component)).length
        : allComponents.size;
  }

  return componentConfigLogicUI(totalCount);
};

const componentConfigLogicUI = (totalCount: number) => ({
  totalCount,
  LogicIcon: WorkflowsIcon,
});

export function getConfigOnComponentWithJSONLogic(components: Array<UiComponentConfig>): Set<{
  component: UiComponentConfig;
  numConfigFields: number;
}> {
  const componentsWithLogic = components
    .map((component: UiComponentConfig, index: number) => {
      if (component == null || component?.config == null) return false;
      const componentConfig = Object.keys(component.config);

      const logicKeysOnConfigSetWithLogic = COMPONENT_JSONLOGIC_KEYS.filter((jsonKey) => {
        if (componentConfig.includes(jsonKey)) {
          const configValue = component.config[jsonKey as keyof (typeof component)['config']];
          return isConfigJSONLogic(configValue);
        }
      });

      return logicKeysOnConfigSetWithLogic.length > 0
        ? { index, component, numConfigFields: logicKeysOnConfigSetWithLogic.length }
        : false;
    })
    .filter(Boolean) as Array<{
    component: UiComponentConfig;
    numConfigFields: number;
  }>;

  return new Set(componentsWithLogic);
}

export const isConfigJSONLogic = (configValue: unknown): configValue is ComponentJsonLogicRule =>
  configValue != null && typeof configValue === 'object';

/**
 * Client data hidden/disabled helpers
 */
export function isHideableComponent(
  component?: UiComponent | undefined,
): component is UiHideableComponent {
  if (component == null || component?.attributes == null) return false;
  return 'hidden' in component.attributes === true;
}

export function isDisabledComponent(
  component: UiComponent,
  clientData: StepPreview | null = null,
): boolean {
  if (!isDisableableComponent(component)) return false;

  const { disabled } = component.attributes;
  return safeApply(disabled ?? false, clientData) ?? false;
}

export function isDisableableComponent(
  component?: UiComponent | undefined,
): component is UiDisableableComponent {
  if (component == null || component?.attributes == null) return false;
  return 'disabled' in component.attributes === true;
}

/**
 * Server config data hidden/disabled helpers
 */

export function isHiddenComponentConfig(
  componentConfig: UiComponentConfig,
  clientData: StepPreview | null = null,
): boolean {
  if (!isHideableComponentConfig(componentConfig)) return false;

  const { 'hidden-server-evaluated': hiddenServer, hidden } = componentConfig.config;
  const hiddenByServerResult = typeof hiddenServer === 'boolean' ? hiddenServer : false;

  // nullish coalesce to false because safeApply will evaluate to null if the first parameter is null
  const hiddenByClientResult =
    typeof hidden === 'boolean'
      ? hidden
      : (safeApply<boolean>(hidden ?? false, clientData) ?? false);

  return hiddenByServerResult || hiddenByClientResult;
}

const HIDEABLE_COMPONENT_CONFIG_KEYS: Array<HideableLogicKey> = [
  'hidden',
  'hidden-server-evaluated',
];

/**
 * Follows required keys defined in
 * app/models/inquiry/ui_component/config/hidable.rb
 */
const isHideableComponentConfig = (
  componentConfig?: UiComponentConfig | undefined,
): componentConfig is UiHideableComponentConfig =>
  componentConfigContainsRequiredJSONLogicConfig(HIDEABLE_COMPONENT_CONFIG_KEYS, componentConfig);

export function isDisabledComponentConfig(
  componentConfig: UiComponentConfig | undefined,
  clientData: StepPreview | null = null,
): boolean {
  if (!isDisableableComponentConfig(componentConfig)) return false;
  const { 'disabled-server-evaluated': disabledServer, disabled } = componentConfig.config;
  const disabledByServerResult = typeof disabledServer === 'boolean' ? disabledServer : false;

  // nullish coalesce to false because safeApply will evaluate to null if the first parameter is null
  const disabledByClientResult =
    typeof disabled === 'boolean'
      ? disabled
      : (safeApply<boolean>(disabled ?? false, clientData) ?? false);

  return disabledByServerResult || disabledByClientResult;
}

const DISABLEABLE_COMPONENT_CONFIG_KEYS: Array<DisableableLogicKey> = [
  'disabled',
  'disabled-server-evaluated',
];

/**
 * Follows required keys defined in
 * app/models/inquiry/ui_component/config/disableable.rb
 */
const isDisableableComponentConfig = (
  componentConfig?: UiComponentConfig | undefined,
): componentConfig is UiDisableableComponentConfig =>
  componentConfigContainsRequiredJSONLogicConfig(
    DISABLEABLE_COMPONENT_CONFIG_KEYS,
    componentConfig,
  );

/**
 * Generic method that validates that required JSONLogic keys for the
 * component setting exist within the component config
 */
function componentConfigContainsRequiredJSONLogicConfig<
  L extends Array<ComponentJsonLogicKey>,
  C extends UiComponentConfig | null | undefined = UiComponentConfig,
>(logicKeys: L, componentConfig: C) {
  if (componentConfig == null || componentConfig?.config == null) return false;
  return logicKeys.every((key) => key in componentConfig.config === true);
}
